import { z } from 'zod';
import { P0_QUESTION_TYPES, totalPoints } from './helpers.js';

/**
 * The quiz model from docs/plans/08-m4-quiz-maker.md.
 *
 * zod is the source of truth: the TypeScript types are inferred from the schemas
 * so a stored blob that validates is guaranteed to match the compiled types.
 * Only the P0 question types are implemented; the P1/P2 members of
 * `QuestionType` are declared so stored data written by a newer build round-trips
 * through validation instead of being silently discarded.
 */

export const questionTypeSchema = z.enum([
  ...P0_QUESTION_TYPES,
  'sequence',
  'dragAndDrop',
  'dropdown',
  'hotspot',
  'shortAnswer',
  'essay',
  'likert',
  'rating',
  'wordBank',
  'dragTheWords',
]);

export type QuestionType = z.infer<typeof questionTypeSchema>;

export const questionMediaSchema = z.object({
  kind: z.enum(['image', 'audio', 'video']),
  mediaId: z.string(),
  altText: z.string().optional(),
});

export const richTextSchema = z.object({
  /** Plain text is authoritative; `html` is an optional presentational upgrade. */
  text: z.string(),
  html: z.string().optional(),
  /** LaTeX fragments rendered with KaTeX in the player. */
  latex: z.array(z.string()).default([]),
});

export const choiceSchema = z.object({
  id: z.string().min(1),
  text: richTextSchema,
  media: questionMediaSchema.optional(),
  isCorrect: z.boolean().default(false),
  feedback: z.string().optional(),
  /** Matching questions only: the id of the target this choice pairs with. */
  matchTargetId: z.string().optional(),
});

export const matchTargetSchema = z.object({
  id: z.string().min(1),
  text: richTextSchema,
});

export const numericRangeSchema = z.object({
  min: z.number(),
  max: z.number(),
});

export const feedbackSchema = z.object({
  correct: z.string().default(''),
  incorrect: z.string().default(''),
  partial: z.string().default(''),
  byAttempt: z.array(z.string()).default([]),
});

export const branchingSchema = z.object({
  onCorrect: z.string().optional(),
  onIncorrect: z.string().optional(),
});

export const accessibilitySchema = z.object({
  altText: z.string().optional(),
  longDescription: z.string().optional(),
});

export const questionSchema = z.object({
  id: z.string().min(1),
  type: questionTypeSchema,
  stem: richTextSchema,
  stemMedia: questionMediaSchema.optional(),
  choices: z.array(choiceSchema).default([]),
  /** Matching questions: the right-hand column. */
  matchTargets: z.array(matchTargetSchema).default([]),
  /** Fill-in-the-blank: accepted strings. Numeric: parsed as numbers. */
  acceptedAnswers: z.array(z.string()).default([]),
  caseSensitive: z.boolean().default(false),
  numericAnswer: z.number().optional(),
  numericTolerance: z.number().default(0),
  numericRange: numericRangeSchema.optional(),
  points: z.number().min(0).default(1),
  partialCredit: z.boolean().default(false),
  attemptsAllowed: z.number().int().min(1).default(1),
  shuffleChoices: z.boolean().default(false),
  timeLimitSeconds: z.number().int().min(0).optional(),
  feedback: feedbackSchema.default({ correct: '', incorrect: '', partial: '', byAttempt: [] }),
  branching: branchingSchema.default({}),
  hint: z.string().optional(),
  explanation: z.string().optional(),
  /** Maps to CPMK / learning outcomes for reporting. */
  tags: z.array(z.string()).default([]),
  difficulty: z.enum(['easy', 'medium', 'hard']).optional(),
  accessibility: accessibilitySchema.default({}),
  /** Reporting id sent to the LMS. Falls back to `id` when unset. */
  reportingId: z.string().optional(),
});

export type Question = z.infer<typeof questionSchema>;
export type Choice = z.infer<typeof choiceSchema>;
export type MatchTarget = z.infer<typeof matchTargetSchema>;
export type RichText = z.infer<typeof richTextSchema>;

export const resultSlideSchema = z.object({
  onPass: z.string().default(''),
  onFail: z.string().default(''),
  showScore: z.boolean().default(true),
  showBreakdown: z.boolean().default(true),
  showTimeSpent: z.boolean().default(false),
  allowRetry: z.boolean().default(true),
  allowPrint: z.boolean().default(false),
});

export const questionPoolSchema = z.object({
  drawCount: z.number().int().min(1),
  fromGroupIds: z.array(z.string()).default([]),
});

export const quizSchema = z.object({
  id: z.string().min(1),
  title: z.string().default(''),
  kind: z.enum(['Graded', 'Survey']).default('Graded'),
  /** Percentage, 0-100. */
  passingScore: z.number().min(0).max(100).default(80),
  scoringMode: z.enum(['HighestAttempt', 'LastAttempt', 'Average']).default('HighestAttempt'),
  questionOrder: z.enum(['Fixed', 'Shuffled']).default('Fixed'),
  questionPool: questionPoolSchema.optional(),
  navigation: z.enum(['Linear', 'FreeWithinQuiz']).default('Linear'),
  allowSkip: z.boolean().default(false),
  allowReview: z.boolean().default(true),
  showCorrectAnswersOnReview: z.boolean().default(true),
  timeLimitSeconds: z.number().int().min(0).optional(),
  attemptsAllowed: z.number().int().min(1).default(1),
  introText: z.string().default(''),
  resultSlide: resultSlideSchema.default({}),
  /** SCORM/xAPI reporting identifier for the quiz as a whole. */
  reportingIdentifier: z.string().optional(),
  requiredForCompletion: z.boolean().default(true),
  questions: z.array(questionSchema).default([]),
});

export type Quiz = z.infer<typeof quizSchema>;
export type ResultSlideConfig = z.infer<typeof resultSlideSchema>;

/** Question types that need at least one choice flagged correct to be gradeable. */
const CHOICE_BASED: readonly QuestionType[] = ['multipleChoice', 'multipleResponse', 'trueFalse'];

export interface ValidationIssue {
  readonly severity: 'error' | 'warning';
  readonly code: string;
  readonly message: string;
  readonly questionId?: string;
}

/**
 * The authoring validation panel. Errors block publishing a graded quiz;
 * warnings are shown but do not block.
 */
export function validateQuiz(quiz: Quiz): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const graded = quiz.kind === 'Graded';

  if (quiz.questions.length === 0) {
    issues.push({ severity: 'error', code: 'quiz.empty', message: 'The quiz has no questions.' });
  }

  const seenIds = new Set<string>();
  for (const q of quiz.questions) {
    if (seenIds.has(q.id)) {
      issues.push({
        severity: 'error',
        code: 'question.duplicate-id',
        message: `Duplicate question id "${q.id}".`,
        questionId: q.id,
      });
    }
    seenIds.add(q.id);

    if (q.stem.text.trim() === '') {
      issues.push({
        severity: 'error',
        code: 'question.empty-stem',
        message: 'The question has no text.',
        questionId: q.id,
      });
    }

    if (graded && CHOICE_BASED.includes(q.type) && !q.choices.some((c) => c.isCorrect)) {
      issues.push({
        severity: 'error',
        code: 'question.no-correct-answer',
        message: 'No choice is marked correct, so this question can never be scored.',
        questionId: q.id,
      });
    }

    if (q.type === 'multipleChoice' && q.choices.filter((c) => c.isCorrect).length > 1) {
      issues.push({
        severity: 'error',
        code: 'question.multiple-correct-in-single-choice',
        message: 'A multiple-choice question has more than one correct answer. Use Multiple Response.',
        questionId: q.id,
      });
    }

    const texts = q.choices.map((c) => c.text.text.trim().toLowerCase()).filter((t) => t !== '');
    if (new Set(texts).size !== texts.length) {
      issues.push({
        severity: 'warning',
        code: 'question.duplicate-choices',
        message: 'Two choices have the same text.',
        questionId: q.id,
      });
    }

    if (q.type === 'fillInTheBlank' && q.acceptedAnswers.length === 0) {
      issues.push({
        severity: 'error',
        code: 'question.no-accepted-answers',
        message: 'A fill-in-the-blank question needs at least one accepted answer.',
        questionId: q.id,
      });
    }

    if (q.type === 'numeric' && q.numericAnswer === undefined && q.numericRange === undefined) {
      issues.push({
        severity: 'error',
        code: 'question.no-numeric-answer',
        message: 'A numeric question needs either an exact answer or an accepted range.',
        questionId: q.id,
      });
    }

    if (q.type === 'matching') {
      const targets = new Set(q.matchTargets.map((t) => t.id));
      if (targets.size === 0) {
        issues.push({
          severity: 'error',
          code: 'question.no-match-targets',
          message: 'A matching question needs targets to match against.',
          questionId: q.id,
        });
      }
      for (const c of q.choices) {
        if (c.matchTargetId !== undefined && !targets.has(c.matchTargetId)) {
          issues.push({
            severity: 'error',
            code: 'question.dangling-match-target',
            message: `Choice "${c.id}" points at a target that does not exist.`,
            questionId: q.id,
          });
        }
      }
    }

    if (graded && q.feedback.correct === '' && q.feedback.incorrect === '') {
      issues.push({
        severity: 'warning',
        code: 'question.no-feedback',
        message: 'A graded question with no feedback tells the learner nothing.',
        questionId: q.id,
      });
    }

    if (q.stemMedia && !q.stemMedia.altText && !q.accessibility.altText) {
      issues.push({
        severity: 'warning',
        code: 'question.missing-alt-text',
        message: 'The question image has no alternative text.',
        questionId: q.id,
      });
    }

    for (const targetId of [q.branching.onCorrect, q.branching.onIncorrect]) {
      if (targetId !== undefined && !quiz.questions.some((other) => other.id === targetId)) {
        issues.push({
          severity: 'error',
          code: 'question.unreachable-branch',
          message: `Branch target "${targetId}" is not a question in this quiz.`,
          questionId: q.id,
        });
      }
    }
  }

  if (graded && totalPoints(quiz) === 0 && quiz.questions.length > 0) {
    issues.push({
      severity: 'error',
      code: 'quiz.zero-points',
      message: 'Every question is worth zero points, so the quiz can never be passed.',
    });
  }

  return issues;
}
